/**
 * 公式引擎 + 大底池生成 - React Hook
 */
import { useMemo, useEffect } from 'react';
import {
  run as runFormulas,
  buildKillPool,
  buildDanPool,
  buildHeatMap,
  buildSmartKillPool,
  BACKTEST,
} from '../lib/formulas.js';
import { generatePools } from '../lib/poolGenerator.js';
import { useHistoryStore } from '../stores/history.js';
import { useUser } from '../stores/user.js';
import { api } from '../lib/api.js';

export function useEngine(userKillsOverride) {
  const history = useHistoryStore(s => s.history);
  const userKillsFromStore = useUser(s => s.user?.userKills || []);
  // 优先用 prop 传入的(向后兼容),否则用 store 里的
  const userKills = userKillsOverride !== undefined ? userKillsOverride : userKillsFromStore;

  // 启动时 + 每 30s 自动从后端拉最新 3D 数据(MAX v7 实时更新)
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    let isFirst = true;
    const API_BASE = 'https://api.3dmfkj.top';
    const fetchLatest = async () => {
      try {
        // 1. 拉最新期(轻量,30s 一次)
        const latestRes = await fetch(`${API_BASE}/api/3d/latest`).then(r => r.json());
        if (cancelled) return;
        if (!latestRes?.ok || !latestRes.latest) return;
        const cur = useHistoryStore.getState().history;
        const curP = cur[0]?.p;
        const newP = latestRes.latest.p;
        // 2. 决定是否拉完整 history
        //   - 第一次:总是拉(用 D1 覆盖 baseHistory 写死的数据)
        //   - 后续:有新期才拉
        const needFull = isFirst || (newP && curP && newP > curP);
        if (!needFull) return;
        const histRes = await fetch(`${API_BASE}/api/3d/history?N=120`).then(r => r.json());
        if (cancelled || !histRes?.ok || !histRes.data || histRes.data.length === 0) return;
        const fresh = histRes.data.map(x => ({
          p: x.p,
          date: x.created_at || x.date,
          a: x.a, b: x.b, c: x.c,
          sum: x.sum, span: x.span, type: x.type,
          s: x.sum, sp: x.span, t: x.type,
        }));
        const merged = [...fresh, ...cur].reduce((acc, x) => {
          if (!acc.find(y => y.p === x.p)) acc.push(x);
          return acc;
        }, []).sort((a, b) => parseInt(b.p) - parseInt(a.p));
        useHistoryStore.setState({ history: merged, updatedAt: Date.now() });
        console.log(`[useEngine 3D] 同步: ${fresh.length} 期,最新 ${fresh[0]?.p} (D1 total: ${latestRes.total})`);
      } catch (e) {
        console.warn('[useEngine 3D] 拉后端失败:', e.message);
      } finally {
        isFirst = false;
      }
    };
    // 立即拉一次
    fetchLatest();
    // 每 30s 拉一次
    timer = setInterval(fetchLatest, 30000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, []);

  return useMemo(() => {
    const result = runFormulas(history);
    const killPool = buildKillPool(result);
    const danPool = buildDanPool(result);
    const smartKill = buildSmartKillPool(result);  // MAX v5: 加权共识杀号
    const heatMap = buildHeatMap(history, 30);
    const hotSet = new Set([...(heatMap?.hot30 || []), ...(heatMap?.hot4 || [])]);
    const pools = generatePools(result, killPool, danPool, {
      userKills,
      hotSet,
      smartKill: smartKill.smartKill,  // 强杀号(总权重 >= 1.0)
      smartKillWeak: smartKill.weakKill,  // 弱杀号(0.5-1.0)
      prevPeriod: history[0],
      history,
    });
    const pool = pools.pool100 || [];
    return { result, killPool, danPool, smartKill, heatMap, pools, pool, history };
  }, [JSON.stringify(userKills), history[0]?.p]);
}

export { BACKTEST };
